// File: apps/agents/src/features/wa_dev/utils/combinedMessageUtils.js

// Import existing utilities we need
import { extractPhoneNumber } from './whatsappUtils';

/**
 * Enhanced extractMessageInfo with direction support
 * @param {object} payload - Message payload
 * @param {string} direction - 'incoming' or 'outgoing'
 * @returns {object} Extracted message info
 */
export const extractMessageInfo = (payload, direction = 'incoming') => {
  if (!payload) return { name: 'Unknown', phone: 'Unknown', body: '', type: 'text' };

  let phone, name, body, type;

  if (direction === 'incoming') {
    phone = extractPhoneNumber(payload.From || payload.from);
    name = payload.ProfileName || phone || 'Unknown';
    body = payload.Body || payload.text || '';
    type = payload.MessageType || 'text';
    if (payload.ButtonText || payload.ButtonPayload) {
      type = 'button';
      body = `🔘 ${payload.ButtonText || payload.ButtonPayload}`;
    }
  } else {
    // Outgoing message structure
    phone = extractPhoneNumber(payload.phoneno || '');
    name = payload.sender || 'PLSSS';
    body = payload.msgbody || '';
    type = 'text';
  }

  return { name, phone, body, type };
};

/**
 * Unified timestamp handler for various Firestore formats
 * @param {any} timestamp - Timestamp in various formats
 * @returns {number} Unix timestamp in milliseconds
 */
export const getUnifiedTimestamp = (timestamp) => {
  if (!timestamp) return Date.now();

  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate().getTime();
  }

  if (timestamp instanceof Date) {
    return timestamp.getTime();
  }

  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const date = new Date(timestamp);
    if (!isNaN(date.getTime())) {
      return date.getTime();
    }
  }

  if (timestamp && typeof timestamp === 'object') {
    if ('_seconds' in timestamp) {
      return (timestamp._seconds * 1000) + (timestamp._nanoseconds || 0) / 1000000;
    } else if ('seconds' in timestamp) {
      return (timestamp.seconds * 1000) + (timestamp.nanoseconds || 0) / 1000000;
    }
  }

  return Date.now();
};

/**
 * Combine incoming and outgoing messages into unified array
 * @param {Array} incomingMsgs - Incoming messages array
 * @param {Array} outgoingMsgs - Outgoing messages array
 * @returns {Array} Combined and sorted messages
 */
export const combineMessages = (incomingMsgs, outgoingMsgs) => {
  const allMessages = [
    ...incomingMsgs.map(msg => {
      const messageInfo = extractMessageInfo(msg.payload);
      return {
        ...msg,
        contact: extractPhoneNumber(msg.meta.from),
        correspondentName: messageInfo.name,
        body: messageInfo.body,
        type: messageInfo.type,
        direction: 'incoming',
        unified_timestamp: getUnifiedTimestamp(msg.timestamp)
      };
    }),
    ...outgoingMsgs.map(msg => ({
      ...msg,
      contact: msg.phone,
      correspondentName: 'Unknown Recipient',
      body: msg.body,
      type: msg.type,
      direction: 'outgoing',
      unified_timestamp: msg.unified_timestamp
    }))
  ];

  allMessages.sort((a, b) => b.unified_timestamp - a.unified_timestamp);
  return allMessages;
};

/**
 * Filter messages for display purposes (doesn't affect name extraction)
 * @param {Array} messages - Combined messages array
 * @param {boolean} filterButtonMessages - Whether to filter out button messages
 * @param {string} searchTerm - Search term for filtering
 * @returns {Array} Filtered messages for display
 */
export const filterMessagesForDisplay = (messages, filterButtonMessages, searchTerm = '') => {
  return messages.filter(msg => {
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const content = (msg.body || '').toLowerCase();
      const phone = msg.contact.toLowerCase();
      const name = (msg.correspondentName || '').toLowerCase();
      
      const matchesSearch = content.includes(searchLower) || 
                           phone.includes(searchLower) || 
                           name.includes(searchLower);
      
      if (!matchesSearch) return false;
    }

    // Apply button message filter only for display
    if (filterButtonMessages && msg.type === 'button') {
      return false;
    }

    return true;
  });
};

/**
 * Filter unified contacts for display purposes (preserves names from hidden messages)
 * @param {Object} unifiedContacts - Unified contacts object
 * @param {boolean} filterButtonMessages - Whether to filter out button messages
 * @param {string} searchTerm - Search term for filtering
 * @returns {Array} Filtered contacts for display
 */
export const filterContactsForDisplay = (unifiedContacts, filterButtonMessages, searchTerm = '') => {
  return Object.values(unifiedContacts).filter(contact => {
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const name = (contact.name || '').toLowerCase();
      const phone = (contact.phone || '').toLowerCase();
      
      const matchesSearch = name.includes(searchLower) || phone.includes(searchLower);
      
      if (!matchesSearch) return false;
    }

    // Check if contact has any non-button messages when filter is active
    if (filterButtonMessages) {
      const hasNonButtonMessages = contact.messages.some(msg => msg.type !== 'button');
      return hasNonButtonMessages;
    }

    return true;
  });
};

/**
 * Group combined messages by phone number with direction support
 * @param {Array} messages - Combined messages array
 * @returns {Object} Grouped messages by phone
 */
export const groupCombinedMessagesByPhone = (messages) => {
  const grouped = {};

  // SINGLE PASS: Group by phone number, track latest incoming name
  messages.forEach(msg => {
    const contact = msg.contact;

    if (!grouped[contact]) {
      grouped[contact] = {
        phone: contact, // Immutable grouping key
        name: contact,  // Default fallback
        messages: [],
        lastMessage: null,
        lastTimestamp: null,
        hasIncoming: false,
        hasOutgoing: false
      };
    }

    // Update name ONLY from incoming messages (latest profile name)
    if (msg.direction === 'incoming') {
      grouped[contact].name = msg.correspondentName; // Always use latest incoming name
      grouped[contact].hasIncoming = true;
    } else if (msg.direction === 'outgoing') {
      grouped[contact].hasOutgoing = true;
    }

    grouped[contact].messages.push(msg);

    // Track latest message for sorting
    if (!grouped[contact].lastTimestamp || msg.unified_timestamp > grouped[contact].lastTimestamp) {
      grouped[contact].lastMessage = msg;
      grouped[contact].lastTimestamp = msg.unified_timestamp;
    }
  });

  return grouped;
};
